import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { DataGrid, type GridColDef, type GridRowSelectionModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  reorderApi, branchesApi,
  type ReorderSuggestionRow, type ReorderSupplierStrategy,
  type CreatedPoSummary,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * UC-M1-PROC-02 — Bulk reorder. Store Manager picks low/OOS items, chooses a supplier
 * strategy, and the system groups by vendor + drafts one PO per supplier.
 */
export function ReorderQueuePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [warehouseId, setWarehouseId] = useState("");
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [strategy, setStrategy] = useState<ReorderSupplierStrategy>("LastSupplier");
  const [resultModal, setResultModal] = useState<{ created: CreatedPoSummary[]; unresolved: number } | null>(null);

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  const suggestions = useQuery({
    queryKey: ["reorder-suggestions", warehouseId],
    queryFn: () => reorderApi.suggestions(warehouseId),
    enabled: !!warehouseId,
  });

  const mutation = useMutation({
    meta: { successMessage: "Purchase orders created" },
    mutationFn: () => reorderApi.createPOs({
      warehouseId, strategy,
      itemIds: selection as string[],
    }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["reorder-suggestions", warehouseId] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setResultModal({ created: data.createdPurchaseOrders, unresolved: data.unresolvedItemIds.length });
      setSelection([]);
    },
  });

  const rows = suggestions.data ?? [];
  const selectedCount = (selection as string[]).length;

  const columns: GridColDef<ReorderSuggestionRow>[] = [
    { field: "isOutOfStock", headerName: "", width: 70,
      renderCell: (p) => p.value ? (
        <Chip size="small" color="error" label="OOS" />
      ) : <Chip size="small" color="warning" label="Low" /> },
    { field: "name", headerName: "Item", flex: 1, minWidth: 200,
      renderCell: (p) => (
        <Stack>
          <Typography variant="body2" fontWeight={500}>{p.row.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.sku} · {[p.row.form, p.row.packaging, p.row.strength].filter(Boolean).join(" · ")}
          </Typography>
        </Stack>
      ) },
    { field: "availableQty", headerName: "Available", width: 110, type: "number" },
    { field: "reorderLevel", headerName: "ROP", width: 90, type: "number" },
    { field: "suggestedQty", headerName: "Suggested PO qty", width: 160, type: "number",
      renderCell: (p) => <Chip size="small" color="primary" label={String(p.value)} /> },
    { field: "lastVendorName", headerName: "Last vendor", flex: 1, minWidth: 160,
      valueGetter: (_v, row) => row.lastVendorName ?? "—" },
    { field: "lastPurchaseRate", headerName: "Last rate", width: 110, type: "number",
      valueFormatter: (v) => v == null ? "—" : inr.format(Number(v)) },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Reorder / low-stock queue
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
          <TextField select label="Pharmacy / warehouse *" size="small" sx={{ minWidth: 280 }}
            value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <MenuItem value="">— Pick branch —</MenuItem>
            {branches.data?.map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Supplier strategy" size="small" sx={{ minWidth: 220 }}
            value={strategy} onChange={(e) => setStrategy(e.target.value as ReorderSupplierStrategy)}>
            <MenuItem value="LastSupplier">Last supplier</MenuItem>
            <MenuItem value="BestRate6Mo">Best rate (6 mo)</MenuItem>
            <MenuItem value="SpecificVendor" disabled>Specific vendor (S5)</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />}
            disabled={!warehouseId || selectedCount === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Generate {selectedCount} → POs
          </Button>
        </Stack>
        {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(mutation.error)}</Alert>}
      </Paper>

      {!warehouseId && (
        <Alert severity="info">Pick a warehouse to load reorder suggestions.</Alert>
      )}

      {warehouseId && (
        <Paper sx={{ p: 2 }}>
          {suggestions.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(suggestions.error)}</Alert>
          )}
          <Box sx={{ height: 600 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={suggestions.isLoading}
              checkboxSelection
              rowSelectionModel={selection}
              onRowSelectionModelChange={setSelection}
              disableRowSelectionOnClick={false}
              getRowId={(row) => row.itemId}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pageSizeOptions={[25, 50, 100]}
              getRowHeight={() => 60}
            />
          </Box>
        </Paper>
      )}

      <Dialog open={!!resultModal} onClose={() => setResultModal(null)} maxWidth="md" fullWidth>
        <ClosableDialogTitle onClose={() => setResultModal(null)}>POs created ({resultModal?.created.length ?? 0})</ClosableDialogTitle>
        <DialogContent>
          {resultModal?.unresolved ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {resultModal.unresolved} item(s) had no resolvable supplier (no prior PO history).
              Add them manually or set a SpecificVendor strategy.
            </Alert>
          ) : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO #</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell align="right">Total qty</TableCell>
                <TableCell align="right">Estimated total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {resultModal?.created.map(p => (
                <TableRow key={p.purchaseOrderId} hover>
                  <TableCell>{p.purchaseOrderId.slice(0, 8)}…</TableCell>
                  <TableCell>{p.vendorName}</TableCell>
                  <TableCell align="right">{p.lineCount}</TableCell>
                  <TableCell align="right">{p.totalQty}</TableCell>
                  <TableCell align="right">{inr.format(p.estimatedTotal)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => {
                      setResultModal(null);
                      navigate(`/procurement/purchase-orders/${p.purchaseOrderId}`);
                    }}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
